import { useEffect, useState } from 'react'
import _ from 'lodash'

import {
  formatNumber,
  getSinglarOrPluralWord,
} from '../../../../utilities/helpers'

import Card from 'react-bootstrap/Card'
import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'

import { QuickpickBadge } from '../../NewSeatedPurchaseWrapper/NewSeatedPurchaseContainer/TicketSelectionPanel/ListingsContainer/TicketGroup/TicketGroup'

import { SEAT_VIEW_PLACEHOLDER } from '../../../../stores/seatmapStore'
import GAPlaceholder from '../../../../assets/placeholder-ga.png'

import './buyingThis.scss'

export default function BuyingThis({
  cart,
  event,
  getTicketImage,
  showBtn = false,
  handleClick,
}) {
  const [ticketGroups, setTicketGroups] = useState([])

  useEffect(() => {
    if (cart?.tickets) {
      const updatedGroups = _.chain(cart.tickets)
        .groupBy(
          (ticket) =>
            `${ticket.sectionId}-${ticket.generalAdmission ? 'GA' : ticket.rowNumber}-${ticket.offer?.id}`
        )
        .map((tickets, key) => {
          const firstTicket = tickets[0]
          return {
            GA: firstTicket.generalAdmission || false,
            sectionId: firstTicket.sectionId,
            sectionNumber: firstTicket.sectionNumber,
            rowNumber: firstTicket.rowNumber,
            tickets: tickets,
            offer: firstTicket.offer,
          }
        })
        .value()

      setTicketGroups(updatedGroups)
    }
  }, [cart, event])

  if (!cart || !cart.tickets || cart.tickets.length === 0) {
    return null
  }

  const getSeats = (seats) => {
    if (seats.length === 0) return ''
    seats.sort((a, b) => a - b)
    const ranges = []
    let start = seats[0]
    let prev = seats[0]

    for (let i = 1; i <= seats.length; i++) {
      if (i === seats.length || seats[i] !== prev + 1) {
        ranges.push(start === prev ? start.toString() : `${start}-${prev}`)
        start = seats[i]
      }
      prev = seats[i]
    }

    // If there's only one range and it's a consecutive range (contains a hyphen)
    if (ranges.length === 1 && ranges[0].includes('-')) {
      return 'Your seats are together 👥'
    }

    return `${getSinglarOrPluralWord(seats?.length, 'Seat')}: ${ranges.join(
      ', '
    )}`
  }

  if (cart?.package) {
    return (
      <div className="buying-this-container">
        <SharedCard
          data={cart}
          getTicketImage={getTicketImage}
          showBtn={showBtn}
          handleClick={handleClick}
          getSeats={getSeats}
          isPackage={true}
          venue={event?.venue}
        />
      </div>
    )
  }

  return (
    <div className="buying-this-container">
      {ticketGroups.map((ticketGroup, i) => {
        return (
          <SharedCard
            key={i}
            data={ticketGroup}
            getTicketImage={getTicketImage}
            showBtn={showBtn}
            handleClick={handleClick}
            getSeats={getSeats}
            isPackage={false}
            venue={event?.venue}
          />
        )
      })}
    </div>
  )
}

const SharedCard = ({
  getTicketImage,
  showBtn,
  handleClick,
  getSeats,
  data,
  isPackage,
  venue,
}) => {
  // User can only purchase season package seats from a single row/section at a time...
  const packageEventCount =
    data?.package?.events?.length ||
    new Set(
      (data?.tickets || [])
        .map((ticket) => ticket.eventUUID || ticket.eventId)
        .filter(Boolean)
    ).size

  const ticketInfo = isPackage
    ? {
        GA: data.tickets[0].generalAdmission,
        sectionId: data.tickets[0].sectionId,
        sectionNumber: data.tickets[0].sectionNumber,
        sectionName: data.tickets[0].sectionName
          ? data.tickets[0].sectionName.toLowerCase()
          : data.tickets[0].sectionNumber.toLowerCase(),
        rowNumber: data.tickets[0].rowNumber,
        tickets: data.tickets.filter(
          (ticket, index, self) =>
            index === self.findIndex((t) => t.seatId === ticket.seatId)
        ),
        count: packageEventCount,
        name: data.package?.name,
      }
    : {
        GA: data.GA,
        sectionId: data.sectionId,
        sectionNumber: data.sectionNumber,
        sectionName: data.sectionName,
        rowNumber: data.rowNumber,
        tickets: data.tickets,
        count: data.tickets?.length,
        name: data.offer?.name,
      }

  const badgeProps = isPackage
    ? {
        isListing: false,
        listing: undefined,
        inventoryType: 'open',
        offerName: ticketInfo.name,
      }
    : {
        isListing: Boolean(data.listing),
        listing: data.listing,
        inventoryType: data.offer?.inventoryType,
        offerName: ticketInfo.name,
      }

  const getIconClass = () => {
    const tickets = ticketInfo.tickets
    if (tickets.some((ticket) => ticket.generalAdmission)) return 'ticket'
    if (tickets.some((ticket) => ticket.accessible)) return 'accessible'
    return 'seat'
  }

  const iconClass = getIconClass()
  const venueSlug =
    venue?.slug ||
    data?.package?.venue?.slug ||
    data?.package?.events?.[0]?.venue?.slug ||
    ticketInfo?.tickets?.[0]?.event?.venue?.slug

  const rawSectionValues = [ticketInfo.sectionNumber, ticketInfo.sectionName]
    .filter(Boolean)
    .map((value) => String(value).trim().toLowerCase())

  const sectionIdentifiers = [
    ...new Set(
      rawSectionValues.flatMap((value) => [
        value,
        value.replace(/^sec(?:tion)?\s*/i, ''),
        value.replace(/\s+/g, ''),
        value.replace(/[^a-z0-9]/g, ''),
      ])
    ),
  ].filter(Boolean)

  const imageCandidates = venueSlug
    ? sectionIdentifiers.flatMap((sectionIdentifier) => [
        getTicketImage(venueSlug, sectionIdentifier, 'thumbnail'),
        getTicketImage(venueSlug, sectionIdentifier, 'seat-view'),
      ])
    : []

  return (
    <Card className="card-xl card-xl--light buying-this">
      <Card.Body className="d-flex-column gap-2">
        <div className="d-flex col-divider">
          <div className="vjx-img-wrap">
            <img
              src={
                ticketInfo?.GA
                  ? GAPlaceholder
                  : imageCandidates[0] || SEAT_VIEW_PLACEHOLDER
              }
              data-fallback-index={0}
              alt="Seat view for this ticket"
              onError={(event) => {
                if (ticketInfo?.GA) {
                  event.currentTarget.src = GAPlaceholder
                  return
                }

                const nextIndex =
                  Number(event.currentTarget.dataset.fallbackIndex || 0) + 1

                if (nextIndex < imageCandidates.length) {
                  event.currentTarget.dataset.fallbackIndex = String(nextIndex)
                  event.currentTarget.src = imageCandidates[nextIndex]
                  return
                }

                event.currentTarget.onerror = null
                event.currentTarget.src = SEAT_VIEW_PLACEHOLDER
              }}
            />
          </div>
          <div className="ticket-info">
            <div className="col">
              <Stack direction="horizontal" gap={2} className="flex-wrap">
                <QuickpickBadge {...badgeProps} />
              </Stack>
              <Stack className="gap-md-2 align-items-start mt-2">
                <Card.Title as="h5" className={`ticket-loc ${iconClass}`}>
                  {ticketInfo.GA
                    ? `${ticketInfo.sectionNumber}`
                    : `Sec ${ticketInfo.sectionNumber} • Row ${ticketInfo.rowNumber}`}
                </Card.Title>

                <div className="tablet-desktop-only small">
                  {ticketInfo.GA ? (
                    <Card.Text>This area is seat-yourself 🧑‍🤝‍🧑</Card.Text>
                  ) : (
                    <Card.Text>
                      {getSeats(
                        ticketInfo.tickets.map((t) => Number(t.seatNumber))
                      )}
                    </Card.Text>
                  )}
                </div>
              </Stack>
            </div>
            <div className="tablet-desktop-only">
              <div className="right-side">
                <div className="ticket-count-info">
                  <span className="ticket-count">
                    {formatNumber(ticketInfo.count)}
                  </span>
                  <span className="ticket-text">
                    {getSinglarOrPluralWord(
                      ticketInfo.count,
                      isPackage ? 'Event' : 'Ticket'
                    )}
                  </span>
                </div>
                {showBtn && (
                  <Button
                    variant="secondary"
                    onClick={handleClick}
                    className="mt-0"
                  >
                    Go to my wallet
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
        <Stack
          direction="horizontal"
          className="mb-1 justify-content-between align-items-center mobile-only small"
        >
          {ticketInfo.GA ? (
            <span>This area is seat-yourself 🧑‍🤝‍🧑</span>
          ) : (
            <span>
              {getSeats(ticketInfo.tickets.map((t) => Number(t.seatNumber)))}
            </span>
          )}
          <span>
            {formatNumber(ticketInfo.count)}{' '}
            <span>
              {getSinglarOrPluralWord(
                ticketInfo.count,
                isPackage ? 'Event' : 'Ticket'
              )}
            </span>
          </span>
        </Stack>
      </Card.Body>
    </Card>
  )
}
